import React from 'react'
import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { TieredPackageLevel, TieredPackageTierId } from 'paintscout'
import classNames from 'classnames'

/**
 * Shared presentational pieces for the Tiered Packages sidebar section + setup wizard.
 * Kept local to the POC so the design can be iterated without touching shared libs.
 */

const useSharedStyles = makeStyles<Theme>(
  (theme) => ({
    // Segmented control (iOS-style track; selected segment is white)
    segTrack: {
      display: 'flex',
      alignItems: 'stretch',
      width: '100%',
      background: theme.paintscout.palette.brand.Neutral.P94,
      borderRadius: theme.borderRadius.lg,
      padding: 3
    },
    segItem: {
      appearance: 'none',
      border: 'none',
      background: 'transparent',
      flex: 1,
      minWidth: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.75, 1),
      borderRadius: theme.borderRadius.sm,
      fontSize: theme.typography.pxToRem(13),
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.paintscout.palette.brand.Neutral.P50,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      transition: 'background-color 0.15s, color 0.15s, box-shadow 0.15s'
    },
    segItemSelected: {
      background: theme.palette.common.white,
      color: theme.paintscout.palette.brand.Neutral.P30,
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.12)',
      borderRadius: theme.borderRadius.lg
    },
    segSep: {
      width: 1,
      alignSelf: 'center',
      height: '55%',
      background: theme.paintscout.palette.brand.Neutral.P85
    },
    segIcon: {
      fontSize: theme.typography.pxToRem(15),
      fontWeight: theme.typography.fontWeightBold,
      display: 'inline-flex',
      alignItems: 'center'
    },

    // Tier level chip (selector card): light-blue pill with a tier mark + level text
    levelChip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      borderRadius: 999,
      padding: theme.spacing(0.25, 1.25),
      background: theme.paintscout.palette.semantic.Info.S95,
      color: theme.palette.primary.main,
      fontSize: theme.typography.pxToRem(13),
      fontWeight: theme.typography.fontWeightMedium,
      lineHeight: 1.5,
      whiteSpace: 'nowrap'
    },

    // Generic small pill (e.g. "Current Estimate", the Set-Up top tier chip)
    pill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      borderRadius: 999,
      padding: theme.spacing(0.25, 1.25),
      background: theme.paintscout.palette.semantic.Info.S95,
      color: theme.palette.primary.main,
      fontSize: theme.typography.pxToRem(13),
      fontWeight: theme.typography.fontWeightMedium,
      lineHeight: 1.5,
      whiteSpace: 'nowrap'
    }
  }),
  { name: 'TieredPackagesShared' }
)

/** Keyboard handler so role="button" elements are activatable via Enter/Space (WCAG 2.1.1). */
export function activateOnKey(handler?: () => void) {
  return (e: React.KeyboardEvent) => {
    if (handler && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      handler()
    }
  }
}

/** Escape closes a popover — pairs with ClickAwayListener for click-outside. */
export function closeOnEscape(close: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      close()
    }
  }
}

/**
 * Per-tier accent color ramp (light → primary → dark), matching the setup mockups.
 * low/high are hand-picked to bracket the brand primary used for the mid tier.
 */
export function getTierColor(tierId: TieredPackageTierId, theme: Theme): string {
  if (tierId === 'low') {
    return '#9DB8F5' // light periwinkle
  }
  if (tierId === 'high') {
    return '#1E3A6E' // dark navy
  }
  return theme.palette.primary.main // mid
}

/** Decorative "seal/flower" tier marker — 8 rounded petals around a center. */
export function TierMark({ color, size = 18 }: { color: string; size?: number }) {
  const petals = 8
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g fill={color}>
        {Array.from({ length: petals }).map((_, i) => (
          <ellipse key={i} cx={12} cy={5.4} rx={2.7} ry={4} transform={`rotate(${(360 / petals) * i} 12 12)`} />
        ))}
        <circle cx={12} cy={12} r={4.3} />
      </g>
    </svg>
  )
}

/** Light-blue level chip used on the selector cards: tier mark + level label. */
export function TierLevelChip({
  level,
  color,
  className
}: {
  level: TieredPackageLevel
  color: string
  className?: string
}) {
  const classes = useSharedStyles({})
  return (
    <span className={classNames(classes.levelChip, className)}>
      <TierMark color={color} size={14} />
      {level}
    </span>
  )
}

/** Small light-blue pill (optional leading icon) — "Current Estimate", Set-Up tier chip, etc. */
export function Pill({
  children,
  icon,
  className
}: {
  children: React.ReactNode
  icon?: React.ReactNode
  className?: string
}) {
  const classes = useSharedStyles({})
  return (
    <span className={classNames(classes.pill, className)}>
      {icon}
      {children}
    </span>
  )
}

export interface SegmentedOption<T extends string> {
  value: T
  label: React.ReactNode
  /** Optional leading glyph (e.g. "%" / "$"). */
  icon?: React.ReactNode
}

/** iOS-style segmented control — a light track where the selected segment is white. */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  className,
  'data-tour': dataTour
}: {
  options: SegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
  /** Product-tour anchor. This component spreads no props, so it has to be named explicitly. */
  'data-tour'?: string
}) {
  const classes = useSharedStyles({})
  const selectedIndex = options.findIndex((o) => o.value === value)
  return (
    // A radio group, not a tablist: these pick a mode, and none of them owns a tabpanel.
    <div className={classNames(classes.segTrack, className)} role="radiogroup" data-tour={dataTour}>
      {options.map((opt, i) => {
        // Faint separator between two unselected, adjacent segments.
        const showSep = i > 0 && i !== selectedIndex && i - 1 !== selectedIndex
        return (
          <React.Fragment key={opt.value}>
            {showSep && <span className={classes.segSep} />}
            <button
              type="button"
              role="radio"
              aria-checked={opt.value === value}
              className={classNames(classes.segItem, opt.value === value && classes.segItemSelected)}
              onClick={() => onChange(opt.value)}
            >
              {opt.icon != null && <span className={classes.segIcon}>{opt.icon}</span>}
              {opt.label}
            </button>
          </React.Fragment>
        )
      })}
    </div>
  )
}

/** Price formatted via the shared Typography 'price' formatter (respects currency config). */
export function Price({
  value,
  className,
  variant = 'body1',
  showSign = false
}: {
  value: number
  className?: string
  variant?: React.ComponentProps<typeof Typography>['variant']
  showSign?: boolean
}) {
  return (
    <>
      {showSign && value > 0 && <span className={className}>+</span>}
      <Typography
        component="span"
        className={className}
        value={value}
        format={'price'}
        showUnits={true}
        variant={variant}
      />
    </>
  )
}
